import type { ReactNode } from 'react'
import { Link } from 'react-router'

import type { MatchedProcedure, SearchInterpretation } from '../../api/hospitals'
import { GENERALIST_SLUGS } from './destination'
import { amtcDialNumber, amtcDisplayNumber } from '../../lib/amtc'
import { Icon } from '../Icon'

/**
 * What the search understood, shown to the patient (M22-T05/T07).
 *
 * Shared by the hospital results and the specialist search on purpose. It lived
 * inside `HospitalResults.tsx` and the specialist page rendered nothing at all,
 * which is how the same words came to produce two different screens — the owner
 * found it by tapping through. Two copies of a safety message drift, and the
 * half that would quietly stop working is the one that matters least often and
 * most.
 *
 * DISCLOSURE IS NOT DECORATION. When we infer, the patient is entitled to see
 * that we inferred and what we inferred, so they can tell a good answer from a
 * confident wrong one. `interpretation` is present only when the literal search
 * found nothing and the resolver was used, so its presence is itself the signal
 * that a guess was made.
 */

/** A search for `scan` names eight procedures. A sentence names this many and
 * counts the rest; the diagnostics page is where the full list lives. */
const MAX_NAMED = 3

/** "X-Ray, CT Scan, MRI Brain and 2 more", the names set off from the sentence. */
function named(procedures: MatchedProcedure[]): ReactNode {
  const more = procedures.length - MAX_NAMED
  return (
    <>
      <span className="font-medium text-ink-1">
        {procedures
          .slice(0, MAX_NAMED)
          .map((p) => p.name)
          .join(', ')}
      </span>
      {more > 0 && ` and ${more} more`}
    </>
  )
}

export function SearchReading({
  interpretation,
  onEmergency,
  onConcierge,
  onGeneralist,
  subject,
}: {
  interpretation: SearchInterpretation
  onEmergency: () => void
  onConcierge: () => void
  /** Scope the list to a generalist who can assess and refer (ADR-0062,
   * destination 2). Absent on pages that cannot filter by specialty, where the
   * concierge remains the action. */
  onGeneralist?: (specialtySlug: string) => void
  /** What the results are, for the one line that names them. The two pages
   * search different things — hospitals and specialists — and must not claim
   * to have searched the other. */
  subject: 'hospitals' | 'specialists' | 'procedures'
}): ReactNode {
  // An emergency is not a search result. The backend refuses to return a list;
  // this is the half that acts on it (ADR-0035).
  if (interpretation.route === 'emergency') {
    return (
      <div
        role="alert"
        className="mb-3 flex flex-col gap-2 rounded-r3 border border-sos bg-sos-bg p-3"
      >
        <p className="text-sm font-semibold text-sos">
          This sounds like an emergency.
        </p>
        <p className="text-[13px] text-ink-2">
          Do not wait for a search result. Get help now.
        </p>
        <button
          type="button"
          onClick={onEmergency}
          className="self-start rounded-full bg-sos px-3.5 py-2 text-sm font-semibold text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sos"
        >
          Go to emergency help
        </button>
        {/* M33-T02: the onward referral, AFTER the action and as plain text --
            it names where this is treated, it is not a second thing to do. */}
        {interpretation.referral ? (
          <p className="text-[13px] text-ink-2">
            After emergency care, {interpretation.referral.name} follows this up.
          </p>
        ) : null}
      </div>
    )
  }

  // A GENERALIST IS A DESTINATION, NOT A READING (M33-T06). Every symptom now
  // carries General Practice as its safety net, so naming it here would append
  // "we also read General Practice, which no partner hospital staffs" to every
  // single symptom answer. The patient did not ask for a GP; the rule sends them
  // to one, and the sentence that does that says so in its own words.
  const unoffered = interpretation.understood_as.filter(
    (s) => !s.offered && !(GENERALIST_SLUGS as readonly string[]).includes(s.slug),
  )

  // THE ORDERED DECISION (M33-T03, ADR-0062).
  //
  // `understood_as` arrives BEST-FIT FIRST: the resolver sorts by relation
  // (definitive before probable before possible) and then by the curator's
  // `rank_hint`, ahead of any score. This component used to throw that order
  // away and partition on `offered`, so whichever specialty happened to have a
  // doctor became the answer. `shortness of breath` resolves to Respiratory
  // Medicine (nobody), Cardiology (one doctor) and Emergency Medicine (nobody),
  // and the page showed a cardiologist while simultaneously saying AMTC would
  // arrange a respiratory doctor. A staffing accident was reordering clinical
  // priority.
  //
  // Clinical fit is read first now. Availability decides only where the one
  // action points, never what the answer is.
  const best = interpretation.understood_as[0] ?? null

  // The PAGE branches on `searchDestination` for the answers it owns — the chip
  // row and the not-listed door — so they cannot contradict this panel. The
  // panel's own blocks key off `best` directly: a procedure somebody offers is
  // an answer AND its unstaffed specialty is still disclosed (M32-T05), which a
  // single label cannot express.

  // Destination 2 in ADR-0062: a generalist who can assess and refer, used when
  // the best fit has nobody. General Practice is the one the ADR names and
  // M33-T06 is what makes concepts reach it. INTERNAL MEDICINE IS HERE BECAUSE
  // IT IS THE GENERALIST THE PLATFORM ACTUALLY STAFFS TODAY — without it, a
  // query like `heartburn` (Gastroenterology, nobody; Internal Medicine, two
  // doctors) would lose a good answer and be sent to the concierge instead.
  // A generalist is not a "lesser-fit specialty": being the right destination
  // for an unclear presentation is what a generalist is for.
  //
  // IN PREFERENCE ORDER (M33-T06b), not the resolver's: General Practice is
  // appended LAST to every symptom, so reading `understood_as` in order named
  // Internal Medicine whenever both were staffed.
  const staffedGeneralists = GENERALIST_SLUGS.map((slug) =>
    interpretation.understood_as.find((s) => s.offered && s.slug === slug),
  ).filter((s): s is NonNullable<typeof s> => s !== undefined)
  const generalist = staffedGeneralists[0] ?? null
  // GP AND INTERNIST BOTH ANSWER, HOSPITAL BY HOSPITAL (owner, 2026-10-19: an
  // internist only where a hospital has no GP). The list below already IS that
  // answer; a button to one specialty's filter would skip the rule.
  //
  // NOT ON THE PROCEDURES PAGE: a procedure list is not per hospital, and the
  // server lists the GP's procedures alone once a GP is staffed, so there the
  // GP is the one answer and keeps its button.
  const bothGeneralists = staffedGeneralists.length > 1 && subject !== 'procedures'
  // Said as a person, with the right article: "a general practitioner", "an
  // internist" -- not "a Internal Medicine".
  const generalistPerson =
    generalist === null
      ? ''
      : generalist.slug === 'general-practice'
        ? 'a general practitioner'
        : generalist.slug === 'internal-medicine'
          ? 'an internist'
          : `a ${generalist.name} specialist`

  // A QUERY THAT NAMED A PROCEDURE (M32-T05). `x-ray` used to find no hospital
  // at all; the server now answers with who offers it and says which procedures
  // it read. Hospitals are reached through what they offer, so the hospital page
  // is told about the procedures. Specialists are reached through the specialty
  // that performs them, so the specialist page names the procedure and then
  // falls through to the specialty blocks below.
  const procedures =
    interpretation.route === 'procedure' ? (interpretation.procedures ?? []) : []
  const offeredProcedures = procedures.filter((p) => p.offer_count > 0)
  const forHospitals = subject === 'hospitals' && procedures.length > 0
  const forSpecialists = subject !== 'hospitals' && procedures.length > 0


  const dial = amtcDialNumber()
  const shown = amtcDisplayNumber()

  // The same hand-off for a discipline nobody staffs and a procedure nobody
  // offers. One copy of it: the dial button is rendered only from a real number
  // (`lib/phone.ts`), and two copies of that guard would drift.
  const amtcAction =
    dial !== null && shown !== null ? (
      <a
        href={`tel:${dial}`}
        className="mt-2 inline-flex items-center gap-1.5 rounded-r2 border border-accent bg-accent-bg px-3 py-2 text-[13px] font-medium text-accent-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
      >
        <Icon name="phone" size={14} /> {shown}
      </a>
    ) : (
      <button
        type="button"
        onClick={onConcierge}
        className="mt-2 inline-flex items-center gap-1.5 rounded-r2 border border-accent bg-accent-bg px-3 py-2 text-[13px] font-medium text-accent-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
      >
        Ask the AMTC team <Icon name="chevr" size={14} />
      </button>
    )

  // Where a procedure's prices and preparation live. There is no page per
  // procedure; the diagnostics search opens on its name. Shown on the specialist
  // page too: nobody may staff Radiology while five hospitals offer X-Ray.
  const procedureLinks = offeredProcedures.length > 0 && (
    <ul className="mt-1.5 flex flex-col gap-1">
      {offeredProcedures.slice(0, MAX_NAMED).map((p) => (
        <li key={p.slug}>
          <Link
            to={`/diagnostics?q=${encodeURIComponent(p.name)}`}
            className="inline-flex items-center gap-1 text-[13px] font-medium text-accent-ink underline underline-offset-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          >
            See {p.name} prices and preparation <Icon name="chevr" size={14} />
          </Link>
        </li>
      ))}
    </ul>
  )

  return (
    <div className="mb-3 rounded-r3 border border-line-1 bg-bg-sunken p-3">
      {forHospitals && offeredProcedures.length > 0 && (
        <>
          <p className="text-[13px] text-ink-2">
            Showing hospitals that offer {named(offeredProcedures)}.
          </p>
          {procedureLinks}
        </>
      )}
      {forHospitals && offeredProcedures.length === 0 && (
        // The owner ruling of 2026-08-31, below, covers this too: name what we
        // understood and hand over to AMTC. Never announce that nobody offers it.
        <div>
          <p className="text-[13px] text-ink-2">
            We understood you&apos;re looking for {named(procedures)}. The AMTC
            team arranges this directly — they&apos;ll organise it for you.
          </p>
          {amtcAction}
        </div>
      )}
      {forSpecialists && (
        <>
          <p className="text-[13px] text-ink-2">
            You searched for {named(procedures)}.
          </p>
          {procedureLinks}
        </>
      )}
      {best !== null && best.offered && (
        <p
          className={
            forSpecialists ? 'pt-2 text-[13px] text-ink-2' : 'text-[13px] text-ink-2'
          }
        >
          {/* Breadth is a property of what we UNDERSTOOD, never of what is
            staffed. Keyed off `offered` this said "Showing specialists with
            Oncology." to `weight loss` — flat and over-confident, with
            Endocrinology and Infectious Diseases silently dropped. */}
          {interpretation.broadened && interpretation.understood_as.length > 1
            ? `That could relate to several areas. Showing ${subject} with `
            : `Showing ${subject} with `}
          {/* ONE SPECIALTY AT A TIME (M33-T04c, owner 2026-10-14). The list
            holds the best fit alone, so the sentence names it alone; another
            staffed discipline would be a second answer to choose between. */}
          <span className="font-medium text-ink-1">{best.name}</span>
          .{' '}
          {/* ADR-0047 IS A HARD CONSTRAINT: an unstaffed discipline resolves BY
            NAME, so "we do not offer this" can never be read as "we did not
            understand you". ADR-0062 removed its ability to COMPETE, not its
            name — so it is said here with no action of its own. */}
          {unoffered.length > 0 && (
            <>
              We also read{' '}
              <span className="font-medium text-ink-1">
                {unoffered.map((s) => s.name).join(', ')}
              </span>
              , which no partner hospital staffs.
            </>
          )}
        </p>
      )}
      {best !== null && !best.offered && (
        // THE ONE HAND-OFF, AND ITS REASON (M33-T03, ADR-0062).
        //
        // The copy is still an owner ruling (2026-08-31): name what we
        // understood and hand the patient on. ADR-0047's hard constraint is
        // still honoured — an unstaffed discipline resolves BY NAME, so that
        // "we will arrange this" cannot be mistaken for "we did not understand
        // you".
        //
        // What changed is that this no longer competes with a second answer.
        // It used to render beside "Showing specialists with Cardiology",
        // because the two blocks were independent conditionals and a query
        // resolving to both kinds fired both. Naming the unstaffed discipline
        // is now the REASON for the single action offered.
        <div className={forSpecialists ? 'pt-2' : undefined}>
          <p className="text-[13px] text-ink-2">
            We understood you&apos;re looking for{' '}
            <span className="font-medium text-ink-1">
              {unoffered.map((s) => s.name).join(', ')}
            </span>
            .{' '}
            {generalist !== null && onGeneralist !== undefined ? (
              <>
                No partner hospital has one yet —{' '}
                <span className="font-medium text-ink-1">{generalistPerson}</span>{' '}
                can assess you and refer you.
                {bothGeneralists && (
                  <> Where a hospital has no GP, an internist will.</>
                )}
              </>
            ) : (
              <>
                The AMTC team arranges this directly — they&apos;ll organise it
                for you.
              </>
            )}
          </p>
          {/* DESTINATION 2 IS AN APPOINTMENT, NOT A SENTENCE. ADR-0062 calls it
            "a real bookable appointment, not a holding message", and this
            branch shipped with no action at all in review. */}
          {bothGeneralists && onGeneralist !== undefined ? null : generalist !==
              null && onGeneralist !== undefined ? (
            <button
              type="button"
              onClick={() => onGeneralist(generalist.slug)}
              className="mt-2 inline-flex items-center gap-1.5 rounded-r2 border border-accent bg-accent-bg px-3 py-2 text-[13px] font-medium text-accent-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
            >
              See {generalist.name} <Icon name="chevr" size={14} />
            </button>
          ) : (
            amtcAction
          )}
        </div>
      )}
      {best === null && procedures.length === 0 && (
        // ADR-0062: no search returns nothing. `no_match` used to return null
        // here, so a query we could not read at all left the patient on an
        // empty page with no way forward.
        //
        // TWO WAYS ON, IN THE OWNER'S WORDS (M33-T02b, 2026-10-14): a query that
        // is not a symptom and is unclear is asked to clarify, or to call AMTC.
        // Single words like `heavy` or `severe` now land here instead of being
        // handed the first condition they happen to begin.
        <div>
          <p className="text-[13px] text-ink-2">
            We couldn&apos;t match that to a specialty. Please clarify your
            query, or call AMTC.
          </p>
          {amtcAction}
        </div>
      )}
    </div>
  )
}
